import "@/App.css";
import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AIChat from "@/components/AIChat";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import ProtectedRoute from "@/components/ProtectedRoute";
import { getJson } from "@/lib/api";

// Route-level code splitting: everything below Home/Login only downloads
// when the user actually navigates there, instead of bloating the initial bundle.
const Products = lazy(() => import("@/pages/Products"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const FarmerDashboard = lazy(() => import("@/pages/FarmerDashboard"));
const BuyerDashboard = lazy(() => import("@/pages/BuyerDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ExporterDashboard = lazy(() => import("@/pages/ExporterDashboard"));
const DeliveryDashboard = lazy(() => import("@/pages/DeliveryDashboard"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const PolicyPage = lazy(() => import("@/pages/PolicyPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));

function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="animate-pulse text-muted-foreground text-sm">Loading…</div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [maintenance, setMaintenance] = useState(null);

  useEffect(() => {
    getJson("/maintenance-status").then(setMaintenance).catch(() => setMaintenance({ enabled: false }));
  }, []);

  // Handle Emergent Google OAuth session_id in URL fragment
  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={<RouteFallback />}><AuthCallback /></Suspense>;
  }

  const bypassRole = maintenance?.emergency ? user?.role === "super_admin" : (user?.role === "admin" || user?.role === "super_admin");
  if (maintenance?.enabled && !authLoading && !bypassRole) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center bg-background text-foreground">
        <div className="max-w-md">
          <div className="text-5xl mb-4">{maintenance.emergency ? "🚨" : "🌾"}</div>
          <h1 className="font-heading font-bold text-2xl mb-2">{maintenance.emergency ? "Site Unavailable" : "We'll be right back"}</h1>
          <p className="text-muted-foreground">{maintenance.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/account/password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/pages/:slug" element={<PolicyPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard/farmer" element={<ProtectedRoute role="farmer"><FarmerDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/buyer" element={<ProtectedRoute><BuyerDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/exporter" element={<ProtectedRoute role="exporter"><ExporterDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/delivery" element={<ProtectedRoute role="delivery_partner"><DeliveryDashboard /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <AIChat />
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
